import Vue from 'vue';

function normalizeResponse(response) {
  const status = response?.status || response?.statusCode || 500;
  const body = response?.data || response || {};
  return {
    status,
    mensagem: body.mensagem || body.msg || '',
    dados: body.dados || body,
  };
}

function buildDeleteUrl(id, tokenJWT2FA) {
  if (!tokenJWT2FA) {
    return `/oauth/clients/${id}`;
  }
  const query = new URLSearchParams({ tokenJWT2FA }).toString();
  return `/oauth/clients/${id}?${query}`;
}

const initialState = () => ({
  clients: [],
  loading: false,
  authorizeData: null,
});

const getters = {
  getClients: (state) => state.clients,
  isLoading: (state) => state.loading,
  getAuthorizeData: (state) => state.authorizeData,
};

const actions = {
  async validarAuthorizeParams({ commit }, params) {
    commit('SET_LOADING', true);
    try {
      const api = new Vue.prototype.$httpClient();
      const query = new URLSearchParams({
        client_id: params.client_id,
        redirect_uri: params.redirect_uri,
        scope: params.scope || 'openid profile',
        code_challenge: params.code_challenge,
        code_challenge_method: params.code_challenge_method || 'S256',
      }).toString();

      const response = await api.send(`/oauth/validate-authorize?${query}`, 'GET', undefined, undefined, false);
      const normalized = normalizeResponse(response);

      if (normalized.status === 200 && normalized.dados) {
        commit('SET_AUTHORIZE_DATA', normalized.dados);
        return { status: 200, dados: normalized.dados };
      }

      return {
        status: normalized.status,
        msg: normalized.mensagem || 'Erro ao validar autorização',
      };
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async confirmarConsentimento(_, params) {
    const api = new Vue.prototype.$httpClient();
    const response = await api.send('/oauth/consent', 'POST', {
      client_id: params.client_id,
      redirect_uri: params.redirect_uri,
      scope: params.scope,
      state: params.state,
      code_challenge: params.code_challenge,
      code_challenge_method: params.code_challenge_method || 'S256',
    }, undefined, false);

    const normalized = normalizeResponse(response);

    if (normalized.status === 200 && normalized.dados?.code) {
      const redirectUrl = new URL(params.redirect_uri);
      redirectUrl.searchParams.set('code', normalized.dados.code);
      if (params.state) {
        redirectUrl.searchParams.set('state', params.state);
      }
      return { status: 200, redirectUrl: redirectUrl.toString() };
    }

    return {
      status: normalized.status,
      msg: normalized.mensagem || 'Erro ao confirmar consentimento',
    };
  },

  async listarClients({ commit }, { tokenJWT2FA } = {}) {
    commit('SET_LOADING', true);
    try {
      const api = new Vue.prototype.$httpClient();
      const response = await api.send(
        '/oauth/clients',
        'GET',
        tokenJWT2FA ? { tokenJWT2FA } : undefined,
        undefined,
        false,
      );
      const normalized = normalizeResponse(response);

      if (normalized.status === 200) {
        commit('SET_CLIENTS', normalized.dados?.clients || []);
      }
      return normalized;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async listarClientsCatalogo({ commit }) {
    commit('SET_LOADING', true);
    try {
      const api = new Vue.prototype.$httpClient();
      const response = await api.send('/oauth/clients/catalogo', 'GET', undefined, undefined, false);
      const normalized = normalizeResponse(response);

      if (normalized.status === 200) {
        return {
          ...normalized,
          dados: { clients: normalized.dados?.clients || [] },
        };
      }
      return normalized;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async listarAtalhos() {
    const api = new Vue.prototype.$httpClient();
    const response = await api.send('/oauth/clients/atalhos', 'GET', undefined, undefined, false);
    return normalizeResponse(response);
  },

  async criarClient(_, { tokenJWT2FA, ...payload }) {
    const api = new Vue.prototype.$httpClient();
    const response = await api.send(
      '/oauth/clients',
      'POST',
      { ...payload, tokenJWT2FA },
      undefined,
      false,
    );
    return normalizeResponse(response);
  },

  async editarClient(_, { tokenJWT2FA, ...payload }) {
    const api = new Vue.prototype.$httpClient();
    const response = await api.send(
      '/oauth/clients',
      'PUT',
      { ...payload, tokenJWT2FA },
      undefined,
      false,
    );
    return normalizeResponse(response);
  },

  async excluirClient(_, { id, tokenJWT2FA }) {
    const api = new Vue.prototype.$httpClient();
    const response = await api.send(
      buildDeleteUrl(id, tokenJWT2FA),
      'DELETE',
      undefined,
      undefined,
      false,
    );
    return normalizeResponse(response);
  },
};

const mutations = {
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_CLIENTS(state, clients) {
    state.clients = clients;
  },
  SET_AUTHORIZE_DATA(state, data) {
    state.authorizeData = data;
  },
  RESET(state) {
    Object.assign(state, initialState());
  },
};

export default {
  namespaced: true,
  state: initialState(),
  getters,
  actions,
  mutations,
};
